import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent
} from "@mui/material";
import {
  McInput,
  McSelect,
  McIcon,
  McOption,
  McButton,
  McSwitch
} from "@maersk-global/mds-react-wrapper";

/**
 * FilterSection component provides filtering controls for applications
 * 
 * @param {string} searchTerm - Current search term
 * @param {Function} onSearchChange - Handler for search term changes
 * @param {string} statusFilter - Current status filter
 * @param {Function} onStatusFilterChange - Handler for status filter changes
 * @param {string} processTypeFilter - Current process type filter
 * @param {Function} onProcessTypeFilterChange - Handler for process type filter changes
 * @param {boolean} isMvcOn - MVC toggle state
 * @param {Function} onMvcToggle - Handler for MVC toggle changes
 * @param {boolean} isBcaOn - BCA toggle state
 * @param {Function} onBcaToggle - Handler for BCA toggle changes
 * @param {Function} onClearFilters - Handler for clearing all filters
 * @returns {JSX.Element} The rendered filter section
 */
const FilterSection = ({ 
  searchTerm, 
  onSearchChange, 
  statusFilter, 
  onStatusFilterChange, 
  processTypeFilter, 
  onProcessTypeFilterChange,
  isMvcOn,
  onMvcToggle,
  isBcaOn,
  onBcaToggle,
  onClearFilters
}) => {
  return (
    <Card sx={{ 
      mb: 3, 
      border: '1px solid #d9d9d9', 
      boxShadow: 'none', 
      borderRadius: '10px'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <McIcon icon="funnel"/>
            <Typography>Filter Applications</Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'end', flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: '200px', flexGrow: 0.2 }}>
            <McInput
              label="Search Applications"
              placeholder="Search by name, owner, or ID..."
              value={searchTerm}
              input={(e) => onSearchChange(e.target.value)}
              fit="medium"
              icon="magnifying-glass"
              iconposition="start"
            />
          </Box>
          <Box>
            <McSelect
              label="Onboarding Status"
              value={statusFilter}
              optionselected={(e) => onStatusFilterChange(e.detail.value)}
              fit="medium"
              optionsheight="200px"
            >
              <McOption value="All Statuses">All Statuses</McOption>
              <McOption value="Onboarded">Onboarded</McOption>
              <McOption value="Not Onboarded">Not Onboarded</McOption>
              <McOption value="In Progress">In Progress</McOption>
            </McSelect>
          </Box>
          <Box>
            <McSelect
              label="Process Type"
              value={processTypeFilter}
              optionselected={(e) => onProcessTypeFilterChange(e.detail.value)}
              fit="medium"
              optionsheight="200px"
            >
              <McOption value="All Processes">All Processes</McOption>
              <McOption value="Rapid (Green)">Rapid (Green)</McOption>
              <McOption value="Rapid (Red)">Rapid (Red)</McOption>
              <McOption value="Classic Change">Classic Change</McOption>
            </McSelect>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', paddingBottom: '8px' }}>
            <div style={{ display: "flex", gap: "16px" }}>
              <McSwitch checked={isMvcOn} change={onMvcToggle} label="MVC" name="mvc-toggle" />
              <McSwitch checked={isBcaOn} change={onBcaToggle} label="BCA" name="bca-toggle" />
            </div>
          </Box>
          {(searchTerm !== "" || statusFilter !== "All Statuses" || processTypeFilter !== "All Processes" || isMvcOn || isBcaOn) && (
            <Box>
              <McButton
                variant="outlined"
                icon="times"
                onClick={onClearFilters}
                fit="medium"
                appearance="neutral"
              >
                Clear
              </McButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FilterSection;
